import React from 'react';
import type { MealBreakConfig, RateMultiplier } from '@/types';
import { LadderOffsetComparison } from '@/components/config_forms/shared/help-graphics/LadderOffsetComparison';
import { normalizeExtendedTiers } from '@/utils/extendedOvertime';
import { getOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';
import { ExtendedOvertimeSection } from '@/components/config_forms/shared/ExtendedOvertimeSection';
import { NumberStepper } from '@/components/ui/number-stepper';
import { cn } from '@/lib/utils';
import { CONTROL_ROW_BODY_CLASS, CONTROL_ROW_LABEL_CLASS } from '@/components/config_forms/shared/ControlRow';
import { HelpInfoCallout } from '@/components/config_forms/short_turn/components/HelpInfoCallout';

interface LadderOffsetComparisonInjectableParams {
  mealBreakConfig: MealBreakConfig;
  rateMultipliers: RateMultiplier[];
  onMealConfigChange?: (updates: Partial<MealBreakConfig>) => void;
  context?: 'user' | 'client';
}

export function buildLadderOffsetComparisonInjectable({
  mealBreakConfig,
  rateMultipliers,
  onMealConfigChange,
  context = 'user',
}: LadderOffsetComparisonInjectableParams): {
  injectables: Record<string, React.ReactNode>;
} {
  const baseLadder = rateMultipliers
    .filter(m => m.defaultMultiplier != null)
    .sort((a, b) => (a.defaultMultiplier ?? 0) - (b.defaultMultiplier ?? 0))
    .map(m => ({ code: m.code, multiplier: m.defaultMultiplier!, name: m.name, isExtended: false }));

  const highestBaseRate = baseLadder.reduce((max, t) => Math.max(max, t.multiplier), 0);
  const normalizedExtended = mealBreakConfig.extendedOvertimeEnabled
    ? normalizeExtendedTiers(mealBreakConfig.extendedOvertimeTiers ?? [], highestBaseRate)
    : [];

  const extendedLadder = normalizedExtended.map((tier, i) => ({
    code: `EXT_${tier.sortOrder}_${i}`,
    multiplier: tier.rate,
    name: tier.name,
    isExtended: true,
  }));

  const ladder = [...baseLadder, ...extendedLadder];

  const { highestOvertimeRate, highestOvertimeTierName, maxExtendedTiers } =
    getOvertimeLadderInfo(rateMultipliers);

  const maxOffset = Math.min(10, Math.max(1, baseLadder.length - 1));
  const offsetDisabled = baseLadder.length <= 1;
  const offsetValue = mealBreakConfig.overtimeOffset ?? 1;

  const overtimeOffsetRow = onMealConfigChange
    ? React.createElement(
        'div',
        {
          className:
            'px-3 py-2 grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center sm:flex sm:items-center sm:justify-between sm:gap-4',
          'data-anim-audit': 'meal-break-offset-help-overtime-offset-row',
        },
        React.createElement(
          'div',
          { className: 'min-w-0' },
          React.createElement('span', { className: CONTROL_ROW_LABEL_CLASS }, 'Overtime Offset'),
          React.createElement(
            'p',
            { className: cn(CONTROL_ROW_BODY_CLASS, 'hidden sm:block') },
            'Number of overtime ladder steps to shift upward when a meal break is missed.',
          ),
        ),
        React.createElement(
          'div',
          { className: 'flex flex-col items-end' },
          React.createElement(NumberStepper, {
            ariaLabel: 'Meal break overtime offset',
            label: 'Meal break overtime offset',
            value: offsetValue,
            min: 1,
            max: maxOffset,
            disabled: offsetDisabled,
            onChange: (next: number) => onMealConfigChange({ overtimeOffset: next }),
          }),
        ),
        React.createElement(
          'p',
          { className: cn(CONTROL_ROW_BODY_CLASS, 'col-span-2 sm:hidden') },
          'Number of overtime ladder steps to shift upward when a meal break is missed.',
        ),
      )
    : null;

  const extendedOvertimeRow = onMealConfigChange
    ? React.createElement(
        'div',
        { className: 'px-3 py-2' },
        React.createElement(ExtendedOvertimeSection, {
          enabled: mealBreakConfig.extendedOvertimeEnabled ?? false,
          onEnabledChange: (v: boolean) => onMealConfigChange({ extendedOvertimeEnabled: v }),
          tiers: mealBreakConfig.extendedOvertimeTiers ?? [],
          onTiersChange: (tiers) => onMealConfigChange({ extendedOvertimeTiers: tiers }),
          highestOvertimeRate,
          highestOvertimeTierName,
          context,
          maxTiers: maxExtendedTiers,
          labelClassName: CONTROL_ROW_LABEL_CLASS,
          descriptionClassName: CONTROL_ROW_BODY_CLASS,
        }),
      )
    : null;

  const leadingControls = onMealConfigChange ? overtimeOffsetRow : null;
  const additionalControls = onMealConfigChange ? extendedOvertimeRow : null;

  const overtimeActiveCallout = React.createElement(HelpInfoCallout, {
    title: 'Overtime',
    className: 'mb-3',
    children: 'If overtime is already in effect, the offset starts from there, not from straight time.',
  });

  const element = React.createElement(LadderOffsetComparison, {
    ladder,
    deadlineHours: mealBreakConfig.mealDeadlineHours ?? 6,
    stepUpAfterMinutes: mealBreakConfig.stepUpAfterMinutes ?? 0,
    useAdditionalOffset: mealBreakConfig.useAdditionalOffset ?? false,
    recurringDeadline: mealBreakConfig.recurringMealDeadline ?? false,
    overtimeOffset: offsetValue,
    leadingControls,
    additionalControls,
    ladderFooter: overtimeActiveCallout,
    ...(onMealConfigChange ? {
      onUseAdditionalOffsetChange: (value: boolean) => onMealConfigChange({ useAdditionalOffset: value }),
      onStepUpAfterMinutesChange: (value: number | null) => onMealConfigChange({ stepUpAfterMinutes: value ?? 0 }),
    } : {}),
  });

  return {
    injectables: { ladder_offset_comparison: element },
  };
}
